<app-logo-area
  [breadcrumbs]="[
    {'display': 'Content Platform', commands: null},
    {'display': 'Aspera Connectors', commands: ['/o', organization()?.id, 'aspera-endpoints']}]"></app-logo-area>
<main>

  <app-headline-section [title]="title()">
  </app-headline-section>

  @if (error()) {
    <section class="cl-section cl-section-regular">
      <div class="container"><div class="row"><div class="col-12">
        <p class="cl-p-regular cl-color-brick">Could not load this endpoint: {{ error() }}</p>
      </div></div></div>
    </section>
  }

  <!-- ============================================================ endpoint -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row"><div class="col-12">
        <h3 class="cl-h3">Endpoint</h3>
        <table class="cl-table-data">
          <tbody>
            @if (loading()) {
              <tr class="cl-tr-no-data"><td colspan="100"><div class="cl-spinner">Loading ...<i class="fas fa-spinner fa-spin"></i></div></td></tr>
            } @else if (endpoint(); as e) {
              <tr><td>Server</td><td>{{ e.server }}</td></tr>
              <tr><td>Share</td><td>{{ e.shareId ?? 'None: a direct Aspera server' }}</td></tr>
              <tr><td>Fixed egress IP</td><td>{{ e.fixedEgressIp || 'None: transfers leave from any address' }}</td></tr>
              @if (worker(); as w) {
                <tr>
                  <td>Worker</td>
                  <td>
                    <span class="it-worker-state"
                          [class.cl-color-leaf]="w.state === 'READY'"
                          [class.cl-color-sea]="w.state === 'BUSY'"
                          [class.cl-color-honey]="w.state === 'STARTING'">{{ w.state | titlecase }}</span>
                    @if (w.since) { <small class="cl-faded">since <app-date [value]="w.since" /></small> }
                    @if (w.holdUntil) { <small class="cl-faded">· held until <app-date [value]="w.holdUntil" /></small> }
                    @if (w.heldBy) { <small class="cl-faded">· {{ w.heldBy }}</small> }
                    @if (w.message) { <small class="cl-faded">— {{ w.message }}</small> }
                  </td>
                </tr>
              }
              <tr>
                <td>Login</td>
                <td>
                  @if (e.status.loggedIn) {
                    <span class="cl-color-sea">Stored</span>
                  } @else if (e.status.needsMfa) {
                    <span class="cl-color-sun">MFA required — an operator must log in</span>
                  } @else {
                    <span class="cl-color-brick">No login stored</span>
                  }
                  @if (e.status.message) {
                    <small class="cl-faded">— {{ e.status.message }}</small>
                  }
                </td>
              </tr>
              <tr><td>Account</td><td>{{ e.status.username || '—' }}</td></tr>
            } @else if (!error()) {
              <tr class="cl-tr-no-data"><td colspan="100">This endpoint does not exist.</td></tr>
            }
          </tbody>
        </table>

        @if (worker() && ('contentplatform:aspera:Mutation/StartWorker' | isAllowedAnywhere: organization())) {
          <!-- A fixed-IP worker starts in minutes; starting it ahead keeps it 30 minutes for browse, login and sends. -->
          <div class="cl-spacing-box">
            <app-do-button #workerButton [messageField]="workerMessage" (actionTriggered)="startWorker($event)"
                           [disabled]="worker()?.state !== 'OFF'">Start worker</app-do-button>
            <app-message-field #workerMessage></app-message-field>
          </div>
        }
                @if (endpoint() && endpoint()?.kind !== 'aoc' && ('contentplatform:aspera:Mutation/Login' | isAllowedAnywhere: organization())) {
          <!-- As signiant's portal login: the share checks the login and it is stored in the endpoint's secret only when
               the share accepts it. The login dialog asks for it. -->
          <div class="login-box cl-form">
            <button type="button" class="cl-a-btn it-login" (click)="openLogin()">
              {{ endpoint()?.status?.loggedIn ? 'Change login' : 'Log in' }}
            </button>
            @if (loginChecking()) {
              <p class="cl-p-small">{{ workerNote() ?? 'Checking the login from the fixed-address instance.' }}</p>
            }
          </div>
        }
        @if (endpoint()?.kind === 'aoc' && ('contentplatform:aspera:Mutation/SetSession' | isAllowedAnywhere: organization())) {
          <!-- Google sign-in leaves no password: the login dialog takes the refresh token of a signed-in Aspera on Cloud
               session. AoC renews it, and the platform keeps the rotated token. AoC checks it before anything is replaced. -->
          <div class="login-box cl-form">
            <button type="button" class="cl-a-btn it-login" (click)="openSession()">
              {{ endpoint()?.status?.loggedIn ? 'Change login' : 'Log in' }}
            </button>
          </div>
        }
      </div></div>
    </div>
  </section>

  <!-- ============================================================ transfers -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row">
        <div class="col-12 col-lg-6">
          @if (endpoint()?.download; as download) {
            <h3 class="cl-h3">Download</h3>
            <p>What this share hands us, and where it lands.</p>
            <table class="cl-table-data">
              <tbody>
                <tr><td>Received into</td><td>{{ download.autoDownloadDestination }}</td></tr>
                <tr>
                  <td>Receiving</td>
                  <td>Automatic <small class="cl-faded">— swept every {{ download.pollIntervalMinutes }} min</small></td>
                </tr>
                <tr>
                  <!-- The sweep's high-water mark: only files the share lists as modified after this are received.
                       It is the share's own listed time, in the share's clock, so it is shown as listed and not
                       through app-date, which would read it in the viewer's time zone. -->
                  <td>Receiving since</td>
                  <td class="cl-td-time">{{ download.autoDownloadWatermark || '—' }}</td>
                </tr>
              </tbody>
            </table>
          }
        </div>
        <div class="col-12 col-lg-6">
          @if (endpoint()?.upload; as upload) {
            <h3 class="cl-h3">Upload</h3>
            <p>What we send to this share.</p>
            <table class="cl-table-data">
              <tbody>
                <tr><td>Source bucket</td><td>{{ upload.sourceBucket }}</td></tr>
                <tr><td>Sent from</td><td>{{ upload.autoUploadFolder }}</td></tr>
                <tr>
                  <td>Sending</td>
                  <td>Automatic <small class="cl-faded">— swept every {{ upload.pollIntervalMinutes }} min</small></td>
                </tr>
              </tbody>
            </table>
          }
        </div>
      </div>
      <div class="row"><div class="col-12">
        <h3 class="cl-h3">Recent transfers</h3>
        <p>The {{ processLimit }} most recent transfers of this endpoint, both directions; older ones stay in the process store.</p>
        <div class="cl-table-scroll">
          <table class="cl-table-data">
            <thead>
              <tr>
                <th class="cl-table-head">Direction</th>
                <th class="cl-table-head">From → to</th>
                <th class="cl-table-head">State</th>
                <th class="cl-table-head">Message</th>
                <th class="cl-table-head">Started</th>
              </tr>
            </thead>
            <tbody>
              @if (processesLoading()) {
                <tr class="cl-tr-no-data"><td colspan="100"><div class="cl-spinner">Loading ...<i class="fas fa-spinner fa-spin"></i></div></td></tr>
              }
              @if (processesError()) {
                <tr class="cl-tr-no-data"><td colspan="100" class="cl-color-brick">Could not load the transfers: {{ processesError() }}</td></tr>
              }
              @for (process of processes(); track process.id) {
                <tr>
                  <td><span class="cl-badge cl-badge-sm cl-color-steel">{{ process.direction }}</span></td>
                  <td>
                    <p>{{ process.source || '—' }}</p>
                    <p>→ {{ process.destination || '—' }}</p>
                    @if (process.egressIp) {
                      <p>from {{ process.egressIp }}</p>
                    }
                  </td>
                  <td>
                    @if (process.state === 'SUCCESS') {
                      <span class="cl-badge cl-badge-sm cl-color-leaf">Success</span>
                    } @else if (process.state === 'FAILED') {
                      <span class="cl-badge cl-badge-sm cl-color-brick">Failed</span>
                    } @else {
                      <span class="cl-badge cl-badge-sm cl-color-honey">In progress{{ process.percent !== null ? ' ' + process.percent + '%' : '' }}</span>
                    }
                  </td>
                  <td>{{ process.message || '—' }}</td>
                  <td class="cl-td-time"><app-date [value]="process.startDate" empty="—" /></td>
                </tr>
              }
              @if (!processesLoading() && processes().length === 0 && !processesError()) {
                <tr class="cl-tr-no-data"><td colspan="100">Nothing has been transferred through this endpoint yet.</td></tr>
              }
            </tbody>
          </table>
        </div>
      </div></div>
    </div>
  </section>

  <!-- ============================================================ files -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row"><div class="col-12">
        <h3 class="cl-h3">Files on the share</h3>
        @if (folder(); as current) {
          <p>
            <strong>{{ current }}</strong>
            @if (current !== '/') {
              · <a href="" (click)="$event.preventDefault(); up()">up one folder</a>
            }
          </p>
        }
        @if (endpoint()?.browseAsync && browsing()) {
          <p class="cl-p-regular">{{ workerNote() ?? 'Listing on the fixed-address instance.' }}</p>
        }
        @if (browseError()) {
          <p class="cl-p-regular cl-color-brick">Could not list this folder: {{ browseError() }}</p>
        }
        <div class="cl-table-scroll">
          <table class="cl-table-data">
            <thead>
              <tr>
                <th class="cl-table-head">Name</th>
                <th class="cl-table-head">Type</th>
              </tr>
            </thead>
            <tbody>
              @if (browsing()) {
                <tr class="cl-tr-no-data"><td colspan="100"><div class="cl-spinner">Loading ...<i class="fas fa-spinner fa-spin"></i></div></td></tr>
              }
              @for (entry of entries(); track entry.path) {
                <tr>
                  <td>
                    @if (entry.type === 'directory') {
                      <a href="" (click)="$event.preventDefault(); open(entry.path)">{{ entry.name }}/</a>
                    } @else {
                      {{ entry.name }}
                    }
                  </td>
                  <td>{{ entry.type === 'directory' ? 'Folder' : 'File' }}</td>
                </tr>
              }
              @if (!browsing() && folder() !== null && entries().length === 0 && !browseError()) {
                <tr class="cl-tr-no-data"><td colspan="100">This folder is empty.</td></tr>
              }
              @if (!loading() && folder() === null && !error()) {
                <tr class="cl-tr-no-data"><td colspan="100">This endpoint is not used in this environment, so it is not browsed here.</td></tr>
              }
            </tbody>
          </table>
        </div>
      </div></div>
    </div>
  </section>
</main>
